import { Box, Button, Group, Stack, TextInput, useMantineTheme } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import { IconPlus } from '@tabler/icons-react';
import { FieldRemoveControl } from 'app/Private/Clients/Form';
import { Panel } from 'components/Panel';
import { i18nKeys } from 'locales/index';
import { useTranslation } from 'locales/logic';
import { findIndex, hasAtLeast, isDefined, isEmpty, keys, last } from 'remeda';
import type { CreateContactPersonForm } from 'shared/hooks/use-create-contact-person';

type MultiPhoneInputProps = {
  form: UseFormReturnType<CreateContactPersonForm>;
};

export const MultiPhoneInput = ({ form }: MultiPhoneInputProps) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const { phones } = form.values;

  const shouldShowError = keys(form.errors).some(
    key => key.startsWith('phones.') && key.includes('.number'),
  );

  const addPhoneInput = () => {
    form.insertListItem('phones', { number: '' });
  };

  const deletePhoneNumber = (index: number) => {
    const phone = phones?.[index];

    if (isDefined(phone?.id)) {
      const updatedPhones = phones!.map((p, i) => (i === index ? { ...p, _destroy: true } : p));
      form.setFieldValue('phones', updatedPhones);
    } else form.removeListItem('phones', index);
  };

  const canAddPhone = () => {
    if (!hasAtLeast(visiblePhones, 1)) return false;

    const lastPhoneNumber = last(visiblePhones)!.number.trim();
    return isEmpty(lastPhoneNumber) || lastPhoneNumber.length < 10;
  };

  const handlePhoneNumberChange = (
    originalIndex: number,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const input = event.currentTarget.value;
    const filtered = input.replace(/[^0-9+]/g, '');

    if (isEmpty(input.trim())) {
      deletePhoneNumber(originalIndex);
      addPhoneInput();
    }

    form.setFieldValue(`phones.${originalIndex}.number`, filtered);
  };

  const visiblePhones = phones?.filter(phone => !phone._destroy) ?? [];

  const phoneInputFields = visiblePhones.map((phone, visibleIndex) => {
    const originalIndex = findIndex(phones ?? [], p =>
      phone.id ? p.id === phone.id : p === phone,
    );

    return (
      <Group key={phone.id || `phone-${originalIndex}`} align="end" gap="sm">
        <TextInput
          {...form.getInputProps(`phones.${originalIndex}.number`)}
          key={form.key(`phones.${originalIndex}.number`)}
          flex={1}
          maxLength={20}
          label={`${t(i18nKeys.PHONE)} ${visibleIndex + 1}`}
          placeholder={t(i18nKeys.FORM.CLIENT.PLACEHOLDER.PHONE)}
          onChange={event => handlePhoneNumberChange(originalIndex, event)}
          rightSection={
            <FieldRemoveControl
              data={visiblePhones}
              disabled={isEmpty(phone.number.trim())}
              onClear={() => {
                deletePhoneNumber(originalIndex);
                addPhoneInput();
              }}
              onRemove={() => deletePhoneNumber(originalIndex)}
            />
          }
        />
      </Group>
    );
  });

  return (
    <Panel
      title={t(i18nKeys.PHONES)}
      style={{ borderColor: shouldShowError ? theme.colors.error[5] : theme.colors.gray[2] }}
    >
      <Box>
        <Stack gap="sm">
          {phoneInputFields}
          <Group justify="end" mt="lg">
            <Button
              variant="light"
              leftSection={<IconPlus size={16} />}
              onClick={addPhoneInput}
              size="sm"
              disabled={canAddPhone()}
            >
              {t(i18nKeys.FORM.CLIENT.ADD_PHONE)}
            </Button>
          </Group>
        </Stack>
      </Box>
    </Panel>
  );
};
